/**
 * App-update adapter — the native (Android) "a newer build is on your track"
 * check, behind the app's adapter seam (T27.2.9).
 *
 * Why this exists. The prod app BUNDLES the web build into the APK, so
 * refreshing the WebView can't pull new native content — only a store update
 * can. The web freshness check (T29, `version.json` → `useFreshness`) nudges
 * "reload" for the web/PWA surface; this module is the NATIVE counterpart. The
 * two never overlap: native build → "open Play Store" nudge here; web build →
 * reload nudge there.
 *
 * Layered detection — two detectors, OR'd, run CONCURRENTLY. The Play In-App
 * Update API's own availability cache lags the Play Store's Updates list by hours
 * (field-confirmed 2026-06-10: a build was installable in the Play Store yet
 * `getAppUpdateInfo()` reported nothing for >15 min), so it can't be the
 * front-line detector for pilot updates. We run two independent detectors and
 * OR their results:
 *   - PRIMARY — a self-hosted `mobile-latest.json` fetched by ABSOLUTE URL
 *     (`no-store`) and numeric-compared to the baked versionCode
 *     (`__APP_BUILD__.build`). Fast — no Google cache; `publish:android:prod`
 *     writes the file only AFTER a successful Play upload, so it never names a
 *     versionCode the track lacks.
 *   - SECONDARY — `getAppUpdateInfo().updateAvailability === 2`. Independent of
 *     OUR infra, so it covers the "our file/server is down" failure the primary
 *     can't. The `@capawesome/capacitor-app-update` plugin is KEPT, used for
 *     detection only — no flexible download, no in-app restart.
 * Either detector reporting a newer build → `updateAvailable: true`. The single
 * action is the same predictable tap: open the Play Store page (see
 * `AppUpdatePrompt.tsx`).
 *
 * Concurrency + secondary timeout (T27.2.9 fix). The resting freshness word
 * (`useNativeAppUpdate`) and the launch/resume nudge BOTH wait on this call's
 * result, so the secondary — the Play API — must never block or hang it. On a
 * real device `getAppUpdateInfo()` is exactly the laggy/sometimes-never-resolving
 * path the design calls unfit to be front-line. The two detectors therefore run
 * CONCURRENTLY, and the secondary is TIME-BOXED (`secondaryTimeoutMs`, default
 * 3s): a slow or never-resolving Play API resolves the secondary to `false`
 * (no update from it) — logged as a timeout — so the FAST primary's answer is
 * never starved. Semantics are unchanged: `updateAvailable = primary || secondary`,
 * each detector fail-soft + reported via `diagnostics.onCheckError`; name/code
 * still come only from the primary.
 *
 * Platform contract. `checkForAppUpdate()` is a NO-OP everywhere but native
 * Android: on web and iOS it returns immediately WITHOUT importing or invoking
 * the plugin and WITHOUT fetching the file. The call site (`main.tsx`) is
 * therefore platform-free — nothing Capacitor-shaped leaks across the seam into
 * `packages/shared` (CP1–CP4).
 *
 * Portability (CP1–CP4). This file lives in `apps/web` precisely because the
 * `@capawesome/*` plugin import is allowed here, never in `packages/shared`. The
 * plugin is reached ONLY on native Android and ONLY via a dynamic `import()`, so
 * the web bundle never eagerly loads it. Mirrors the `secureStorage.ts` seam.
 *
 * Fail-soft AND logged. Each detector is wrapped in its own `try/catch`: a
 * thrown detector never breaks app launch, never throws into the caller, and
 * never aborts the OTHER detector — but it is ALWAYS reported through the
 * injectable `diagnostics.onCheckError` (no silent `catch{}`), mirroring
 * `secureStorage.ts`'s injectable diagnostics so a broken layer is observable.
 *
 * Testability. `checkForAppUpdate()` takes its platform probe, its plugin, its
 * fetch impl, its baked versionCode, and its diagnostics as injectable options,
 * so the two-detector OR, the no-op contract, the fail-soft path, and the
 * dismissal logic are all pinned by unit tests with no device and no real
 * plugin. The defaults wire the real probe, the dynamically-imported plugin,
 * the global `fetch`, and `__APP_BUILD__.build`.
 */

import { isNativePlatform } from '../routes/nativeGoogleSignIn';

/**
 * Absolute URL of the self-hosted version manifest the PRIMARY detector reads.
 * Absolute (not a relative `/mobile-latest.json`) on purpose: in the native
 * WebView the origin is `https://android.shopista.ca`, so a relative path would resolve to
 * the build's OWN baked file — exactly why the native check can't reuse the web
 * `version.json`. The web origin serves this with `no-store` + a CORS allow for
 * the WebView origin (see apps/web/nginx.conf + publish:android:prod).
 */
export const MOBILE_LATEST_URL = 'https://app.shopista.ca/mobile-latest.json';

/**
 * Capawesome `updateAvailability` value meaning a higher versionCode exists on
 * the user's track. The SECONDARY detector treats only this value as "newer".
 */
export const UPDATE_AVAILABLE = 2;

/**
 * How long the SECONDARY (Play API) detector may run before it is treated as
 * "no update from the secondary". The Play `getAppUpdateInfo()` call can lag for
 * seconds or never resolve on a real device; a few seconds is enough for a
 * healthy response yet short enough that the FAST primary's answer (and the
 * resting freshness word / launch nudge that wait on it) is never starved.
 */
export const DEFAULT_SECONDARY_TIMEOUT_MS = 3000;

/**
 * The shape the self-hosted `mobile-latest.json` carries — written by
 * `publish:android:prod` from the same `git rev-list --count HEAD` the app bakes
 * as its versionCode, so the comparison is self-consistent.
 */
export interface MobileLatest {
  versionCode: number;
  versionName: string;
}

/**
 * The minimal slice of the Capawesome `@capawesome/capacitor-app-update` surface
 * the SECONDARY detector uses. Detection only — no flexible-download / restart
 * methods (the prior cut's flexible flow is dropped). Kept as an interface (not
 * the plugin directly) so a fake is injectable in tests.
 */
export interface AppUpdatePlugin {
  /** Ask Play whether a newer build is available on the user's track. */
  getAppUpdateInfo(): Promise<{ updateAvailability: number }>;
}

/**
 * Optional diagnostics for the update check. Receives a short label naming which
 * detector failed and the (redacted-at-the-callsite) error — mirrors
 * `secureStorage.ts`'s injectable diagnostics so a broken detector is OBSERVABLE
 * rather than silently swallowed. Default no-op; `main.tsx` wires a console
 * diagnostic on native.
 */
export interface AppUpdateDiagnostics {
  onCheckError?: (detector: string, error: unknown) => void;
}

export interface AppUpdateResult {
  /** True if EITHER detector reports a newer build on the track. */
  updateAvailable: boolean;
  /**
   * The `versionName` advertised by the primary (`mobile-latest.json`) when it
   * reported newer; null when only the secondary fired (the plugin doesn't
   * surface a name) or no update is available.
   */
  latestVersionName: string | null;
  /**
   * The newer `versionCode` advertised by the primary, when it reported newer;
   * null when only the secondary fired (the Play API surfaces no number) or no
   * update is available. The nudge keys its per-version dismissal on this so a
   * dismissed nudge re-appears only when a HIGHER versionCode is advertised.
   */
  latestVersionCode: number | null;
}

export interface CheckForAppUpdateOptions {
  /**
   * Platform probe. Defaults to the real `isNativePlatform()` (true only inside
   * the Capacitor shell). Tests inject `() => false` (web) or a fake-true to
   * drive the native branch.
   */
  isNative?: () => boolean;
  /**
   * Plugin override for the SECONDARY detector. Defaults to the dynamically-
   * imported Capawesome plugin (reached only on native Android). Tests inject a
   * fake `AppUpdatePlugin`.
   */
  plugin?: AppUpdatePlugin;
  /**
   * Fetch impl for the PRIMARY detector. Defaults to the global `fetch`. Tests
   * inject a fake to drive the file detector without a network.
   */
  fetchImpl?: typeof fetch;
  /**
   * The running build's versionCode (the baked `__APP_BUILD__.build`, a numeric
   * string). Defaults to the baked value; tests inject a fixture. The PRIMARY
   * detector compares the advertised `versionCode` against this number.
   */
  currentVersionCode?: number;
  /** Absolute URL of the self-hosted manifest. Defaults to MOBILE_LATEST_URL. */
  manifestUrl?: string;
  /**
   * Time-box (ms) for the SECONDARY detector. After this elapses, the slow /
   * never-resolving Play API is treated as "no update from the secondary" (logged
   * as a timeout) so it can neither block nor hang the result. Defaults to
   * `DEFAULT_SECONDARY_TIMEOUT_MS` (3s).
   */
  secondaryTimeoutMs?: number;
  /**
   * Timer seam for the secondary's time-box. Defaults to the global
   * `setTimeout`/`clearTimeout`. Tests inject these (with vitest fake timers) so
   * the timeout fires deterministically without real wall-clock waits.
   */
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
  /** Redacted diagnostics (detector label + error). Default no-op. */
  diagnostics?: AppUpdateDiagnostics;
}

/**
 * Run the two-detector update check.
 *
 * Native Android: fetch the absolute `mobile-latest.json` and numeric-compare
 * its `versionCode` to the baked one (PRIMARY); AND ask `getAppUpdateInfo()`
 * whether Play reports an update (SECONDARY). OR them. Each detector is isolated
 * in its own `try/catch` — one failing never aborts the other and never throws
 * out — and any failure is reported via `diagnostics.onCheckError`.
 *
 * Web / iOS: a NO-OP — returns `{ updateAvailable: false, latestVersionName:
 * null }` immediately, without importing or invoking the plugin and without
 * fetching. This is the contract the unit test pins.
 */
export async function checkForAppUpdate(
  options: CheckForAppUpdateOptions = {},
): Promise<AppUpdateResult> {
  const isNative = options.isNative ?? isNativePlatform;

  const none: AppUpdateResult = {
    updateAvailable: false,
    latestVersionName: null,
    latestVersionCode: null,
  };

  // The platform gate is the whole no-op contract: off native Android we return
  // before fetching OR touching the plugin, so web/iOS never import native code
  // and never hit the network for this check.
  if (!isNative()) return none;

  const diag = options.diagnostics ?? {};
  const currentVersionCode = options.currentVersionCode ?? bakedVersionCode();
  const manifestUrl = options.manifestUrl ?? MOBILE_LATEST_URL;
  const secondaryTimeoutMs =
    options.secondaryTimeoutMs ?? DEFAULT_SECONDARY_TIMEOUT_MS;
  const setTimeoutImpl = options.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = options.clearTimeoutImpl ?? clearTimeout;

  // Run BOTH detectors concurrently — the secondary (Play API) must never block
  // the FAST primary. The secondary is time-boxed so a slow / never-resolving
  // `getAppUpdateInfo()` resolves to `false` (logged) instead of hanging the
  // whole result, which the resting freshness word + launch nudge both wait on.
  const [primary, secondary] = await Promise.all([
    // PRIMARY — self-hosted file vs baked versionCode. Isolated + logged.
    checkPrimary(options.fetchImpl ?? fetch, manifestUrl, currentVersionCode, diag),
    // SECONDARY — Play API availability, time-boxed. Isolated + logged. Runs
    // regardless of the primary (covers a down primary, the point of having it).
    checkSecondaryWithTimeout(
      options.plugin,
      diag,
      secondaryTimeoutMs,
      setTimeoutImpl,
      clearTimeoutImpl,
    ),
  ]);

  return {
    updateAvailable: primary.updateAvailable || secondary,
    // Only the primary carries a version NAME / numeric code; the secondary
    // (Play API) surfaces neither.
    latestVersionName: primary.latestVersionName,
    latestVersionCode: primary.latestVersionCode,
  };
}

/**
 * PRIMARY detector — fetch the absolute manifest (`no-store`) and numeric-
 * compare. Fail-soft: a network error, non-OK status, or unparseable body is
 * reported and treated as "no update from the primary" (the secondary still
 * runs). Returns the advertised name when newer so the nudge can show it.
 */
async function checkPrimary(
  fetchImpl: typeof fetch,
  url: string,
  currentVersionCode: number,
  diag: AppUpdateDiagnostics,
): Promise<{
  updateAvailable: boolean;
  latestVersionName: string | null;
  latestVersionCode: number | null;
}> {
  const empty = {
    updateAvailable: false,
    latestVersionName: null,
    latestVersionCode: null,
  };
  try {
    const res = await fetchImpl(url, { cache: 'no-store' });
    if (!res.ok) return empty;
    const body = (await res.json()) as Partial<MobileLatest>;
    const advertised =
      typeof body.versionCode === 'number' ? body.versionCode : NaN;
    if (!Number.isInteger(advertised)) return empty;
    const newer = advertised > currentVersionCode;
    if (!newer) return empty;
    return {
      updateAvailable: true,
      latestVersionName:
        typeof body.versionName === 'string' ? body.versionName : null,
      latestVersionCode: advertised,
    };
  } catch (error) {
    // Down primary (offline, file 404, CORS, bad JSON) is expected to fail —
    // the secondary covers it — but it must be OBSERVABLE, not silent.
    diag.onCheckError?.('primary', error);
    return empty;
  }
}

/**
 * SECONDARY detector, TIME-BOXED. Races the raw secondary against a timeout: if
 * `getAppUpdateInfo()` is slow or never resolves (the field-confirmed Play-API
 * lag), the timeout wins and resolves `false` (no update from the secondary),
 * reported via `diagnostics.onCheckError` so the stall is observable. This is
 * what keeps the result — and the resting freshness word / launch nudge waiting
 * on it — from hanging on the Play API. The losing branch is left to settle
 * harmlessly (its result/error is ignored once the race is decided).
 */
function checkSecondaryWithTimeout(
  injected: AppUpdatePlugin | undefined,
  diag: AppUpdateDiagnostics,
  timeoutMs: number,
  setTimeoutImpl: typeof setTimeout,
  clearTimeoutImpl: typeof clearTimeout,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeoutImpl(timer);
      resolve(value);
    };

    const timer = setTimeoutImpl(() => {
      if (settled) return;
      // The Play API didn't answer in time — treat as no update from the
      // secondary, but log it (no silent swallow); the FAST primary still drives
      // the result.
      diag.onCheckError?.(
        'secondary',
        new Error(`Play update check timed out after ${timeoutMs}ms`),
      );
      settle(false);
    }, timeoutMs);

    void checkSecondary(injected, diag).then(settle);
  });
}

/**
 * SECONDARY detector — Play API availability. Lazily loads the plugin only when
 * no fake was injected (so web never reaches the dynamic import — but this is
 * only ever called on the native branch). Fail-soft + logged.
 */
async function checkSecondary(
  injected: AppUpdatePlugin | undefined,
  diag: AppUpdateDiagnostics,
): Promise<boolean> {
  try {
    const plugin = injected ?? (await loadAppUpdatePlugin());
    const info = await plugin.getAppUpdateInfo();
    return info.updateAvailability === UPDATE_AVAILABLE;
  } catch (error) {
    // No Play track (sideloaded / debug build), the cache-lag null, or any
    // plugin error: best-effort, must never break launch — but logged.
    diag.onCheckError?.('secondary', error);
    return false;
  }
}

/**
 * The running build's versionCode as a number. The baked `__APP_BUILD__.build`
 * is a numeric string (the git commit count — see vite.config.ts); parse it,
 * flooring an unexpected non-number to 0 so the comparison never throws.
 */
function bakedVersionCode(): number {
  const n = Number.parseInt(__APP_BUILD__.build, 10);
  return Number.isInteger(n) ? n : 0;
}

/**
 * Wrap the raw Capacitor plugin proxy in a plain, NON-THENABLE facade. The
 * `@capacitor/core` proxy forwards EVERY property access to the native bridge —
 * including `.then`. So if an `async` function RETURNS the proxy (or you `await`
 * it), the Promise machinery probes `.then` on the resolved value and invokes the
 * non-existent native `AppUpdate.then()`, throwing `"AppUpdate.then()" is not
 * implemented on android`. That silently broke the SECONDARY detector on every
 * launch (the 3s time-box then masked it as a stall). The facade exposes ONLY
 * this seam's interface method and has no `.then`, so it is safe to await/return.
 * The arrow references `raw` LAZILY (only inside the call), so building the facade
 * never touches `raw.then`. Mirrors the secureStorage.ts seam. Exported for the
 * regression test.
 */
export function wrapAppUpdatePlugin(raw: AppUpdatePlugin): AppUpdatePlugin {
  return {
    getAppUpdateInfo: () => raw.getAppUpdateInfo(),
  };
}

/**
 * Native-only loader for the Capawesome `AppUpdate` plugin. Dynamically imported
 * so the web bundle never eagerly pulls native code (CP1); reached only on the
 * native branch above. This is the ONLY place the plugin module is referenced.
 * Returns the NON-THENABLE facade, never the raw proxy (see `wrapAppUpdatePlugin`).
 */
async function loadAppUpdatePlugin(): Promise<AppUpdatePlugin> {
  const { AppUpdate } = await import('@capawesome/capacitor-app-update');
  return wrapAppUpdatePlugin(AppUpdate as unknown as AppUpdatePlugin);
}
